import inboxIcon from '@/assets/dashboard/inbox.svg'
import coinIcon from '@/assets/dashboard/coin.svg'
import tagIcon from '@/assets/dashboard/tag.svg'
import { Card } from '@/components/ui/card'
import { formatCompactNumber, formatMoney } from '@/utils'
import { StatsCard } from '../stats/StatsCard'
import { BuySellSplit } from '../BuySellSplit'
import { ProfileWelcomeCard } from '../ProfileWelcomeCard'
import { GatedNotice } from '@/components/gate'
import { DashboardStatsProps } from './DashboardStatsBase'
import { statValue } from './DashboardStatsValue'

/**
 * Mobile version of Dashboard Stats
 * 2-column grid with specific row spans
 *
 * `ProfileWelcomeCard` is rendered here too, as the first row.
 *
 * It used to be desktop-only — the desktop grid gives it a whole 264px column
 * and the mobile layout had nowhere to put it. Mobile now gets the card's own
 * compact variant (a 32px avatar and one line of text, ~56px tall) above the
 * stats row rather than beside it: it is the greeting, so it belongs at the
 * top of the page on both breakpoints, and stacking costs one short row where
 * a column would have cost a third of the screen. The alternative considered
 * and rejected was the header avatar — it is already there, it is not a
 * greeting, and the brief asks for a welcome prompt, not another avatar.
 */
export const DashboardStatsMobile = ({
  profile,
  totalProjects,
  totalOrderbookValue,
  averageDiscount,
  buyOrders,
  sellOrders,
  loading = false,
  financialsHidden = false,
  listedOrders,
}: DashboardStatsProps) => {
  const showListedOrders = financialsHidden && totalProjects === null && listedOrders !== undefined

  return (
    <div className="flex flex-col gap-[6px]">
      {/* Row 0: the greeting */}
      <ProfileWelcomeCard profile={profile} />

      {/* Row 1: Stats Cards - 3 columns in one row with gap-[6px] between cards */}
      <div className="grid grid-cols-3 gap-[6px]">
        <StatsCard
          icon={inboxIcon}
          iconAlt={showListedOrders ? 'Listed Orders' : 'Total Projects'}
          label={showListedOrders ? 'Listed\nOrders' : 'Total\nProjects'}
          value={
            showListedOrders
              ? formatCompactNumber(listedOrders)
              : statValue({
                  value: totalProjects,
                  financialsHidden,
                  label: 'Total projects',
                  widthCh: 3,
                  render: value => formatCompactNumber(value),
                })
          }
          loading={loading}
          mobileLayout="gap"
          mobileHeight="h-auto"
          mobileContentHeight="h-auto"
        />
        <StatsCard
          icon={coinIcon}
          iconAlt="Order Book"
          label={"Order\nBook"}
          value={statValue({
            value: totalOrderbookValue,
            financialsHidden,
            label: 'Total order book value',
            widthCh: 4,
            render: value => (
              <span className="whitespace-nowrap">
                <span className="text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">$</span>
                <span> {formatMoney(value).replace('$', '').trim()}</span>
              </span>
            ),
          })}
          loading={loading}
          mobileLayout="gap"
          mobileHeight="h-auto"
        />
        <StatsCard
          icon={tagIcon}
          iconAlt="Average Discount"
          label={"Average\nDiscount"}
          value={statValue({
            value: averageDiscount,
            financialsHidden,
            label: 'Average discount',
            widthCh: 3,
            render: value => (
              <>
                <span>{value.toFixed(1)} </span>
                <span className="text-[12.9px] text-[rgba(45,41,35,0.48)] dark:text-[rgba(253,253,253,0.48)]">%</span>
              </>
            ),
          })}
          loading={loading}
          mobileLayout="gap"
          mobileHeight="h-auto"
          mobileContentHeight="h-auto"
        />
      </div>

      {/* Row 2: Buy/Sell Split - full width, exact Figma sizes: p-[16px], h-[70px] */}
      <div>
        <Card variant="default" padding="none" className="bg-white dark:bg-[#1c1c1c] border border-solid border-white dark:border-[#1c1c1c] p-[16px]">
          {/* Never pass undefined or 0/0 counts — see the desktop variant. */}
          {buyOrders !== null && sellOrders !== null && buyOrders + sellOrders > 0 ? (
            <BuySellSplit
              buyCount={buyOrders}
              sellCount={sellOrders}
            />
          ) : (
            <GatedNotice
              title="Buy / sell split"
              description={
                financialsHidden
                  ? 'Opens up with verification.'
                  : buyOrders === 0 && sellOrders === 0
                    ? 'No orders on the book right now.'
                    : 'Could not load just now.'
              }
              actionable={financialsHidden}
            />
          )}
        </Card>
      </div>
    </div>
  )
}
